import { useState, useCallback, useEffect, useRef } from 'react';
import { callCloudRun } from '../lib/cloudRun';
import { summariseActivity } from '../lib/activitySummary';
import type { MesoActivity } from '../lib/activityCalendar';
import type { QuantitativeScoreData } from '../components/QuantitativeScores';
import { hasCoachOversight, type UserRole } from '@rhwb/types';
import type { QuantitativeScoreRow, Runner, Coach } from '@rhwb/types';

interface Option {
  value: string;
  label: string;
}

interface DashboardDataState {
  // Widget data
  data: QuantitativeScoreData[];
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Cumulative score
  cumulativeScore: number | null;

  // Activity summary
  activitySummary: {
    mileage: { percent: number | null; planned: number | null; completed: number | null };
    strength: { percent: number | null; planned: number | null; completed: number | null };
  };

  // Training feedback
  trainingFeedback: Array<{ meso: string; qual: string }>;

  // Day-by-day workouts behind the activity summary, per finished meso
  activityCalendar: MesoActivity[];

  // Last data refresh date
  lastDataRefresh: string | null;

  // Data fetching functions
  fetchWidgetData: () => Promise<void>;
  fetchCumulativeScore: () => Promise<void>;
  fetchActivitySummary: () => Promise<void>;
  fetchTrainingFeedback: () => Promise<void>;

  // Lists fetching
  searchRunners: (query: string, setSearchResults: (results: Option[]) => void) => Promise<void>;
}

export const useDashboardData = (
  email: string,
  userRole: UserRole | undefined,
  season: string,
  selectedRunner: string,
  selectedCoach: string,
  hybridToggle: 'myScore' | 'myCohorts',
  coachName: string,
  setCoachList: (list: Option[]) => void,
  setRunnerList: (list: Option[]) => void,
  setCoachName: (name: string) => void
): DashboardDataState => {
  const [data, setData] = useState<QuantitativeScoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const shouldAutoFetch = useRef(false);
  
  // Track if coach list has been fetched to prevent re-fetching on re-renders
  const coachListFetchedRef = useRef(false);
  const lastFetchedRoleRef = useRef<string | null>(null);
  const isFetchingCoachesRef = useRef(false);
  
  // Track runner fetching - use a fetch ID to handle cancellation properly
  const runnerFetchIdRef = useRef(0);
  
  // Track if lists have been cleared for runner role to prevent infinite loops
  const runnerListsClearedRef = useRef(false);
  const lastRunnerRoleRef = useRef<UserRole | undefined>(undefined);

  const [cumulativeScore, setCumulativeScore] = useState<number | null>(null);
  const [activitySummary, setActivitySummary] = useState<{
    mileage: { percent: number | null; planned: number | null; completed: number | null };
    strength: { percent: number | null; planned: number | null; completed: number | null };
  }>({
    mileage: { percent: null, planned: null, completed: null },
    strength: { percent: null, planned: null, completed: null }
  });
  const [trainingFeedback, setTrainingFeedback] = useState<Array<{ meso: string; qual: string }>>([]);
  const [activityCalendar, setActivityCalendar] = useState<MesoActivity[]>([]);
  const [lastDataRefresh, setLastDataRefresh] = useState<string | null>(null);

  // Fetch last data refresh date from Cloud Run
  useEffect(() => {
    const fetchLastDataRefresh = async () => {
      try {
        const data = await callCloudRun<{ last_activity_date?: string }>('/api/dashboard/status');
        if (data?.last_activity_date) setLastDataRefresh(data.last_activity_date);
      } catch (err) {
        console.error('[DASHBOARD DATA] Unexpected error fetching last data refresh:', err);
      }
    };
    fetchLastDataRefresh();
  }, []);

  // Add a timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && email) {
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading, email]);

  // Fetch coach name for coach/hybrid roles
  useEffect(() => {
    const fetchCoachNameInternal = async () => {
      if ((userRole === 'coach' || userRole === 'hybrid') && email) {
        try {
          const data = await callCloudRun<{ coach: string | null }>('/api/coaches/name', { email });
          if (data?.coach) setCoachName(data.coach);
        } catch (err) {
          console.error('Unexpected error fetching coach name:', err);
          setError('Unable to load coach information. Please refresh the page.');
        }
      }
    };
    fetchCoachNameInternal();
  }, [userRole, email, setCoachName, setError]);

  // EFFECT 1: Fetch coach list for admin (only runs once per session)
  useEffect(() => {
    if (!season || !userRole) {
      return;
    }
    
    if (!hasCoachOversight(userRole)) {
      return;
    }

    // Skip if already fetched or currently fetching
    if (coachListFetchedRef.current && lastFetchedRoleRef.current === 'admin') {
      console.log('[DASHBOARD DATA] Coach list already fetched for admin, skipping');
      return;
    }
    if (isFetchingCoachesRef.current) {
      console.log('[DASHBOARD DATA] Already fetching coaches, skipping');
      return;
    }

    const fetchCoachList = async () => {
      isFetchingCoachesRef.current = true;
      console.log('[DASHBOARD DATA] Fetching ALL coaches for admin...');
      try {
        const result = await callCloudRun<{ coaches: Pick<Coach, 'coach'>[] }>('/api/coaches/list');
        const coaches = result?.coaches ?? [];
        const uniqueCoaches = Array.from(new Set(coaches.map((r) => r.coach))).filter(Boolean) as string[];
        const coachOptions = uniqueCoaches.map((c) => ({ value: c, label: c })).sort((a, b) => a.label.localeCompare(b.label));
        console.log('[DASHBOARD DATA] Setting coach list:', coachOptions.length, 'coaches');
        setCoachList(coachOptions);
        coachListFetchedRef.current = true;
        lastFetchedRoleRef.current = 'admin';
        isFetchingCoachesRef.current = false;
      } catch (err: any) {
        isFetchingCoachesRef.current = false;
        console.error('[DASHBOARD DATA] Exception fetching coaches:', err);
        setError(`Unable to load coaches list: ${err?.message || 'Unknown error'}`);
      }
    };

    fetchCoachList();
  }, [season, userRole, setCoachList, setError]);

  // EFFECT 2: Fetch runners when admin selects a coach (runs on selectedCoach change)
  useEffect(() => {
    if (!season || !hasCoachOversight(userRole)) {
      return;
    }

    if (selectedCoach) {
      // Increment fetch ID to track this specific fetch
      runnerFetchIdRef.current += 1;
      const currentFetchId = runnerFetchIdRef.current;
      
      const seasonNo = Number(season);
      console.log('[DASHBOARD DATA] Fetching runners for coach:', selectedCoach, 'season:', seasonNo, 'fetchId:', currentFetchId);

      const fetchRunners = async () => {
        try {
          const result = await callCloudRun<{ runners: Runner[] }>('/api/runners/for-coach', { season: seasonNo, coach: selectedCoach });
          if (currentFetchId !== runnerFetchIdRef.current) {
            console.log('[DASHBOARD DATA] Stale runner fetch, ignoring results');
            return;
          }
          const runnerOptions = (result?.runners ?? []).map((r: Runner) => ({ value: r.email_id, label: r.runner_name })).sort((a: Option, b: Option) => a.label.localeCompare(b.label));
          console.log('[DASHBOARD DATA] Setting runner list:', runnerOptions.length, 'runners');
          setRunnerList(runnerOptions);
        } catch (err: any) {
          console.error('[DASHBOARD DATA] Exception fetching runners:', err);
          setError(`Unable to load runners: ${err?.message || 'Unknown error'}`);
        }
      };

      fetchRunners();
    } else {
      console.log('[DASHBOARD DATA] No coach selected, clearing runner list');
      setRunnerList([]);
    }
  }, [season, userRole, selectedCoach, setRunnerList, setError]);

  // EFFECT 3: Fetch runner lists for coach/hybrid roles
  useEffect(() => {
    if (!season || !userRole) {
      return;
    }
    
    // This effect is only for coach and hybrid roles
    if (userRole !== 'coach' && !(userRole === 'hybrid' && hybridToggle === 'myCohorts')) {
      return;
    }

    let isCancelled = false;

    const fetchRunnersForCoachRole = async () => {
      const coach = coachName;
      if (!coach) { setRunnerList([]); return; }
      const seasonNo = Number(season);
      console.log('[DASHBOARD DATA] Fetching runners for coach role:', coach, 'season:', seasonNo);
      try {
        const result = await callCloudRun<{ runners: Runner[] }>('/api/runners/for-coach', { season: seasonNo, coach });
        if (isCancelled) { console.log('[DASHBOARD DATA] Fetch cancelled, ignoring runner results'); return; }
        const runnerOptions = (result?.runners ?? []).map((r: Runner) => ({ value: r.email_id, label: r.runner_name })).sort((a: Option, b: Option) => a.label.localeCompare(b.label));
        console.log('[DASHBOARD DATA] Setting runner list:', runnerOptions.length, 'runners');
        setRunnerList(runnerOptions);
      } catch (err: any) {
        console.error('[DASHBOARD DATA] Exception fetching runners:', err);
        setError(`Unable to load runners: ${err?.message || 'Unknown error'}`);
      }
    };

    fetchRunnersForCoachRole();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, userRole, hybridToggle, coachName]);

  // EFFECT 4: Clear lists for runner role (only once when role changes to runner)
  useEffect(() => {
    // Only clear lists if role changed to runner and we haven't cleared them yet
    if (userRole === 'runner' && lastRunnerRoleRef.current !== 'runner') {
      console.log('[DASHBOARD DATA] User is runner, clearing lists');
      setCoachList([]);
      setRunnerList([]);
      runnerListsClearedRef.current = true;
      lastRunnerRoleRef.current = 'runner';
    } else if (userRole !== 'runner') {
      // Reset the flag when role changes away from runner
      runnerListsClearedRef.current = false;
      lastRunnerRoleRef.current = userRole;
    }
  }, [userRole, setCoachList, setRunnerList]);

  // Search runners for admin users
  const searchRunners = async (query: string, setSearchResults: (results: Option[]) => void) => {
    if (!query.trim() || !hasCoachOversight(userRole)) { setSearchResults([]); return; }
    try {
      const result = await callCloudRun<{ runners: { email_id: string; full_name: string }[] }>('/api/runners/search', { query });
      const options = (result?.runners ?? []).map(r => ({ value: r.email_id, label: r.full_name || r.email_id }));
      setSearchResults(options.sort((a: Option, b: Option) => a.label.localeCompare(b.label)));
    } catch (err) {
      console.error('Error searching runners:', err);
      setError('Unable to search runners. Please try again.');
      setSearchResults([]);
    }
  };

  // Fetch widget data for a specific runner
  const fetchWidgetDataForRunner = useCallback(async (runnerEmail: string) => {
    setLoading(true);
    setError(null);

    const targetEmail = (userRole === 'hybrid' && hybridToggle !== 'myCohorts') ? email : runnerEmail;
    if ((hasCoachOversight(userRole) || userRole === 'coach' || (userRole === 'hybrid' && hybridToggle === 'myCohorts')) && !runnerEmail) {
      setData([]);
      setLoading(false);
      return;
    }

    let rows: QuantitativeScoreRow[] = [];
    try {
      const result = await callCloudRun<{ data: QuantitativeScoreRow[] }>('/api/scores/quantitative', { season: `Season ${season}`, email: targetEmail });
      rows = result?.data ?? [];
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
      return;
    }

    const formattedQuant: QuantitativeScoreData[] = (rows || []).map((row: QuantitativeScoreRow) => ({
      meso: row.meso,
      personal: row.quant_personal ?? 0,
      coach: row.quant_coach ?? 0,
      raceDistance: row.quant_race_distance ?? 0,
    }));

    setData(formattedQuant);
    setLoading(false);
  }, [season, email, userRole, hybridToggle]);

  // Fetch widget data
  const fetchWidgetData = useCallback(async () => {
    // Hybrid users always need a fetch on toggle: fetchWidgetDataForRunner
    // resolves the right target email for both myScore (their own) and
    // myCohorts (the selected runner, or empty if none picked yet).
    if (userRole === 'runner' || userRole === 'hybrid' || selectedRunner) {
      await fetchWidgetDataForRunner(selectedRunner || email);
    }
  }, [email, userRole, selectedRunner, fetchWidgetDataForRunner]);

  // Fetch cumulative score
  const fetchCumulativeScore = useCallback(async () => {
    const runnerEmail = selectedRunner || email;
    if (!runnerEmail) {
      setCumulativeScore(null);
      return;
    }

    let rows: { meso: string; cumulative_score: number }[] = [];
    try {
      const result = await callCloudRun<{ data: typeof rows }>('/api/scores/cumulative', { season: `Season ${season}`, email: runnerEmail });
      rows = result?.data ?? [];
    } catch (err) {
      console.error('Error fetching cumulative score:', err);
      setError('Unable to load cumulative score. Please try again.');
      setCumulativeScore(null);
      return;
    }

    if (!rows || rows.length === 0) {
      setCumulativeScore(null);
      return;
    }

    const latest = rows.reduce((max, row) => {
      const mesoNum = parseInt((row.meso || '').replace(/[^0-9]/g, ''), 10);
      const maxNum = parseInt((max.meso || '').replace(/[^0-9]/g, ''), 10);
      return mesoNum > maxNum ? row : max;
    }, rows[0]);
    setCumulativeScore(latest.cumulative_score);
  }, [selectedRunner, email, season]);

  // Fetch activity summary
  const fetchActivitySummary = useCallback(async () => {
    const runnerEmail = selectedRunner || email;
    if (!runnerEmail) {
      setActivitySummary({
        mileage: { percent: null, planned: null, completed: null },
        strength: { percent: null, planned: null, completed: null }
      });
      return;
    }

    let rows: { category: string; percent_completed: number; planned: number; completed: number }[] = [];
    try {
      const result = await callCloudRun<{ data: typeof rows }>('/api/scores/activity-summary', { season: `Season ${season}`, email: runnerEmail });
      rows = result?.data ?? [];
    } catch (err) {
      console.error('Error fetching activity summary:', err);
      setError('Unable to load activity summary. Please try again.');
      setActivitySummary({ mileage: { percent: null, planned: null, completed: null }, strength: { percent: null, planned: null, completed: null } });
      return;
    }

    if (!rows || rows.length === 0) {
      setActivitySummary({
        mileage: { percent: null, planned: null, completed: null },
        strength: { percent: null, planned: null, completed: null }
      });
      return;
    }

    setActivitySummary(summariseActivity(rows));
  }, [selectedRunner, email, season]);

  // Fetch training feedback via Cloud Run (qual scores in Cloud SQL — HIPAA)
  const fetchTrainingFeedback = useCallback(async () => {
    const runnerEmail = selectedRunner || email;
    if (!runnerEmail || !season) {
      setTrainingFeedback([]);
      return;
    }

    try {
      const result = await callCloudRun<{ data: Array<{ meso: string; qual: string }> }>(
        '/api/qual-scores',
        { season: `Season ${season}`, runnerEmail }
      );
      const rows: Array<{ meso: string; qual: string }> = result?.data || [];

      if (rows.length === 0) {
        setTrainingFeedback([]);
        return;
      }

      // Data comes pre-sorted from the API, but sort client-side as safety net
      const sortedRows = rows.sort((a, b) => {
        const mesoA = parseInt(a.meso.replace(/[^0-9]/g, ''), 10);
        const mesoB = parseInt(b.meso.replace(/[^0-9]/g, ''), 10);
        return mesoB - mesoA;
      });
      setTrainingFeedback(sortedRows);
    } catch (err: any) {
      console.error('Error fetching training feedback:', err);
      setTrainingFeedback([]);
    }
  }, [selectedRunner, email, season]);

  // Fetch the activity calendar. It only explains the activity summary, so a
  // failure hides the breakdown rather than raising a dashboard error.
  useEffect(() => {
    const runnerEmail = selectedRunner || email;
    if (!runnerEmail || !season) {
      setActivityCalendar([]);
      return;
    }

    let isCancelled = false;
    const fetchActivityCalendar = async () => {
      try {
        const result = await callCloudRun<{ data: MesoActivity[] }>(
          '/api/scores/activity-calendar',
          { season: `Season ${season}`, email: runnerEmail }
        );
        if (!isCancelled) setActivityCalendar(result?.data ?? []);
      } catch (err) {
        console.error('Error fetching activity calendar:', err);
        if (!isCancelled) setActivityCalendar([]);
      }
    };
    fetchActivityCalendar();

    return () => {
      isCancelled = true;
    };
  }, [selectedRunner, email, season]);

  // On initial app load
  useEffect(() => {
    if (email && userRole && initialLoad) {
      if (userRole === 'runner') {
        fetchWidgetData();
      }
      setInitialLoad(false);
      shouldAutoFetch.current = true;
    }
    if (email && userRole && !initialLoad && loading) {
      if (userRole !== 'runner' && !selectedRunner) {
        setLoading(false);
      }
    }
  }, [email, userRole, initialLoad, loading, selectedRunner, fetchWidgetData]);

  // Auto-fetch data when filters change
  useEffect(() => {
    if (shouldAutoFetch.current && !initialLoad) {
      fetchWidgetData();
    }
  }, [season, selectedRunner, selectedCoach, hybridToggle, initialLoad, fetchWidgetData]);

  // Fetch cumulative score when dependencies change
  useEffect(() => {
    fetchCumulativeScore();
  }, [fetchCumulativeScore]);

  // Fetch activity summary when dependencies change
  useEffect(() => {
    fetchActivitySummary();
  }, [fetchActivitySummary]);

  // Fetch training feedback when dependencies change
  useEffect(() => {
    fetchTrainingFeedback();
  }, [fetchTrainingFeedback]);

  return {
    data,
    loading,
    error,
    setError,
    cumulativeScore,
    activitySummary,
    trainingFeedback,
    activityCalendar,
    lastDataRefresh,
    fetchWidgetData,
    fetchCumulativeScore,
    fetchActivitySummary,
    fetchTrainingFeedback,
    searchRunners
  };
};
